import React, { useContext, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { AppContext } from '../../App';
import { updateProductStatus } from '../../clients/project_client';
import { showToast } from '../../config_and_helpers/helpers';
import { PROJECT_PRODUCT_STATUS_OPTIONS } from '../../config_and_helpers/config';
import DropdownSelect from '../Generic/DropdownSelect';
import { logError } from '../../config_and_helpers/logger';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';

/**
 * Represents a modal component for updating the status of a product within a project.
 *
 * @param {string} projectId - The ID of the project.
 * @param {number} [productId] - Inventory product id (legacy).
 * @param {number} [projectProductId] - Project line id (required for ad-hoc lines).
 * @param {string} currentStatus - The current status of the product.
 * @param {function} onStatusUpdate - Callback function called after successful status update.
 */
const ProjectProductStatusModal = ({
  projectId,
  productId,
  projectProductId,
  currentStatus,
  onStatusUpdate,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState(currentStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setShowModal(false);
    setStatus(currentStatus); // Reset to current status on close
  };

  const handleShow = () => {
    setShowModal(true);
    setStatus(currentStatus);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (status === currentStatus) {
      showToast('Status unchanged', 'info', 'top-right', 2000);
      handleClose();
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await updateProductStatus(
        token,
        projectId,
        productId,
        status,
        projectProductId
      );

      if (response && response.success) {
        if (response.toast) {
          response.toast();
        }
        handleClose();
        if (onStatusUpdate) {
          onStatusUpdate();
        }
      } else {
        const errorMessage =
          response?.messages?.length > 0
            ? response.messages.join(', ')
            : 'Failed to update product status';
        showToast(errorMessage, 'error', 'top-right', 5000);
      }
    } catch (error) {
      logError('ProjectProductStatusModal', 'updateProductStatus failed', error);
      showToast(
        'An error occurred while updating the product status',
        'error',
        'top-right',
        3000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isViewer) {
    return null;
  }

  return (
    <>
      <Button variant="outline-primary" size="sm" onClick={handleShow}>
        Update Status
      </Button>

      <Modal
        show={showModal}
        onHide={handleClose}
        dialogClassName="procure-modal-glass procure-modal-glass-form"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>Update Product Status</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="procure-modal__body">
            <Form.Group className="mb-3">
              <Form.Label>Current Status</Form.Label>
              <Form.Control
                type="text"
                value={currentStatus}
                disabled
                className="bg-light"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Status</Form.Label>
              <DropdownSelect
                optionsList={PROJECT_PRODUCT_STATUS_OPTIONS}
                label="New Status"
                selectedValue={
                  PROJECT_PRODUCT_STATUS_OPTIONS.find(s => s.value === status) ||
                  null
                }
                setSelectedValue={option =>
                  setStatus(option?.value || currentStatus)
                }
                placeholder="Select new status"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="procure-modal__footer">
            <Button
              variant="secondary"
              className="procure-modal__close-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="glass-primary-btn"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Status'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectProductStatusModal;

